// M&A Phase 1 deal lens — per-lever C0/C1 dedup.
//
// Split out of ma-deal-lens.ts (#5220 engineering condition: keep the new production
// file under the 600-line ceiling) rather than left inline, because both compositions
// that price a deal's synergy register need this EXACT dedup, on the EXACT same
// tie-break: `buildCases` in `ma-deal-lens.ts` (the pure `@helix/lib` DTO) and
// `composeMaDealLens` in `ma-synergy-tracking.deal-lens-envelope.ts` (the API-side
// EV/operating-income bridge the masyn adapter reads). One file, one algorithm — the two
// compositions can never quietly disagree about which of a colliding C0/C1 pair counts.

import {
  isGovernedSynergyCertaintyClass,
  type SynergyCertaintyClass,
  type SynergyLeverCommitment,
} from './ma-synergy-commitment';
import { type SynergyCandidateMapEntry } from './ma-synergy-candidate-map';
import { decisionConsoleBetKey } from './decision-console';

export type MaDealLensSynergyEntrySelection = Readonly<{
  entries: readonly SynergyCandidateMapEntry[];
  separationPendingLeverIds: readonly string[];
}>;

/**
 * One lever contributes once. The admitted packet may legitimately carry BOTH a
 * C0-observed and a C1-committed record for the same synergy lever — the loader preserves
 * several governed commitments per lever by design (see
 * ma-synergy-tracking.source.ts:107-125) — so without this dedup a single lever's dollars
 * would price twice in every case and in the operating-income bridge. Only the two
 * GOVERNED classes (c0_observed, c1_committed_lever) can collide this way; a
 * c2_modelled_upside record for the same lever is a separate register row by design and is
 * NEVER folded into this dedup.
 *
 * The tie-break mirrors the loader's own canonical commitment identity — the governed
 * decision plus the realization metric (`decisionConsoleBetKey`, the same construction
 * `expectedCommitmentBetKey` uses; never a second spelling here). The entry whose
 * commitment resolves a real governed identity wins; when both or neither resolve one,
 * the more advanced governance posture (committed over merely observed) wins.
 */
function dedupeGovernedEntriesByLever(
  counting: readonly SynergyCandidateMapEntry[],
  commitmentsByLeverAndClass: ReadonlyMap<string, SynergyLeverCommitment>
): readonly SynergyCandidateMapEntry[] {
  const winnerByLever = new Map<string, SynergyCandidateMapEntry>();
  for (const entry of counting) {
    if (!isGovernedSynergyCertaintyClass(entry.certaintyClass)) continue;
    const existing = winnerByLever.get(entry.synergyLeverId);
    winnerByLever.set(
      entry.synergyLeverId,
      existing ? strongerGovernedEntry(existing, entry, commitmentsByLeverAndClass) : entry
    );
  }

  return counting.filter(
    (entry) =>
      !isGovernedSynergyCertaintyClass(entry.certaintyClass) ||
      winnerByLever.get(entry.synergyLeverId) === entry
  );
}

function strongerGovernedEntry(
  left: SynergyCandidateMapEntry,
  right: SynergyCandidateMapEntry,
  commitmentsByLeverAndClass: ReadonlyMap<string, SynergyLeverCommitment>
): SynergyCandidateMapEntry {
  const leftIdentity = governedRealizationIdentity(left, commitmentsByLeverAndClass);
  const rightIdentity = governedRealizationIdentity(right, commitmentsByLeverAndClass);
  if (leftIdentity && !rightIdentity) return left;
  if (rightIdentity && !leftIdentity) return right;
  if (left.certaintyClass === right.certaintyClass) return right;
  return left.certaintyClass === 'c1_committed_lever' ? left : right;
}

function governedRealizationIdentity(
  entry: SynergyCandidateMapEntry,
  commitmentsByLeverAndClass: ReadonlyMap<string, SynergyLeverCommitment>
): string {
  const commitment = commitmentsByLeverAndClass.get(
    leverClassKey(entry.synergyLeverId, entry.certaintyClass)
  );
  const decisionId = commitment?.governedCommitmentDecisionId?.trim();
  const metricKey = commitment?.realizationEvidence.metricKey?.trim();
  return decisionId && metricKey ? decisionConsoleBetKey(decisionId, metricKey) : '';
}

function leverClassKey(synergyLeverId: string, certaintyClass: SynergyCertaintyClass): string {
  return `${synergyLeverId}:${certaintyClass}`;
}

function crossRegisterCollisionLeverIds(
  entries: readonly SynergyCandidateMapEntry[]
): readonly string[] {
  const membership = new Map<string, Set<'governed' | 'modeled'>>();
  for (const entry of entries) {
    const register = isGovernedSynergyCertaintyClass(entry.certaintyClass)
      ? 'governed'
      : entry.certaintyClass === 'c2_modelled_upside'
        ? 'modeled'
        : null;
    if (!register) continue;
    const registers = membership.get(entry.synergyLeverId) ?? new Set<'governed' | 'modeled'>();
    registers.add(register);
    membership.set(entry.synergyLeverId, registers);
  }
  return [...membership.entries()]
    .filter(([, registers]) => registers.size > 1)
    .map(([synergyLeverId]) => synergyLeverId)
    .sort();
}

export function selectMaDealLensSynergyEntries(
  entries: readonly SynergyCandidateMapEntry[],
  commitments: readonly SynergyLeverCommitment[]
): MaDealLensSynergyEntrySelection {
  const commitmentsByLeverAndClass = new Map(
    commitments.map((c) => [leverClassKey(c.synergyLeverId, c.certaintyClass), c])
  );
  const governedDeduped = dedupeGovernedEntriesByLever(entries, commitmentsByLeverAndClass);
  const separationPendingLeverIds = crossRegisterCollisionLeverIds(governedDeduped);
  const pending = new Set(separationPendingLeverIds);
  return {
    entries: governedDeduped.filter((entry) => !pending.has(entry.synergyLeverId)),
    separationPendingLeverIds,
  };
}

/**
 * The one entry point every composition prices a synergy register through. Takes the raw
 * commitments (not a pre-built map) so a caller never has to reconstruct
 * `commitmentsByLeverAndClass` itself and risk a second, slightly different key
 * construction — `buildCases` (this package's own `buildMaDealLens`) and
 * `composeMaDealLens` (the API-side EV/operating-income bridge in
 * `ma-synergy-tracking.deal-lens-envelope.ts`) both call this directly.
 */
export function dedupeGovernedSynergyEntriesByLever(
  entries: readonly SynergyCandidateMapEntry[],
  commitments: readonly SynergyLeverCommitment[]
): readonly SynergyCandidateMapEntry[] {
  return selectMaDealLensSynergyEntries(entries, commitments).entries;
}
